import type { ReportedMonitorListItem } from '@simplemon/lib';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { jobsApi } from '@/api/jobs';
import { Button } from '@/components/ui/button';
import { JobOutcome, JobTime } from './presentation';

const historyMessages = {
  success: 'All checks passed',
  mixed: 'Some checks failed',
  failure: 'All checks failed',
  empty: 'No completed checks',
} as const;

const historyColors = {
  success: 'bg-[#22A06B]',
  mixed: 'bg-[#E9A23B]',
  failure: 'bg-[#DE5B5B]',
  empty: 'bg-gray-200',
} as const;

function historyDate(date: string, options: Intl.DateTimeFormatOptions) {
  const [year, month, day] = date.split('-').map(Number);
  return new Intl.DateTimeFormat(undefined, { ...options, timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, day)),
  );
}

function HistoryDay({
  day,
  index,
  total,
}: {
  day: ReportedMonitorListItem['dailyHistory'][number];
  index: number;
  total: number;
}) {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const message = historyMessages[day.status];
  return (
    <div className="relative flex h-7 w-7 items-center justify-center">
      <button
        type="button"
        aria-label={`${historyDate(day.date, { dateStyle: 'full' })}: ${message}`}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
        }}
        className="flex h-7 w-7 items-center justify-center rounded focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-600"
      >
        <span aria-hidden="true" className={`h-4 w-4 rounded-sm ${historyColors[day.status]}`} />
      </button>
      {open && (
        <span
          id={tooltipId}
          role="tooltip"
          className={`pointer-events-none absolute bottom-full z-30 mb-1 w-max rounded bg-gray-900 px-2 py-1 text-xs font-normal text-white shadow ${index === 0 ? 'left-0' : index === total - 1 ? 'right-0' : 'left-1/2 -translate-x-1/2'}`}
        >
          {message}
        </span>
      )}
    </div>
  );
}

export function JobsPage() {
  const [jobs, setJobs] = useState<ReportedMonitorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setJobs(await jobsApi.list());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="px-4 py-8 sm:px-6 lg:px-8">
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b px-6 py-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Jobs</h1>
            <p className="mt-1 text-sm text-gray-600">Reported job results and run history</p>
          </div>
          <Button variant="outline" onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        </div>
        {loading ? (
          <output className="flex items-center justify-center gap-2 px-6 py-12 text-gray-600">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading jobs…
          </output>
        ) : error ? (
          <div className="px-6 py-10" role="alert">
            <p className="text-sm text-red-700">{error}</p>
            <Button className="mt-4" variant="outline" onClick={() => void load()}>
              Retry
            </Button>
          </div>
        ) : jobs.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-gray-600">
            No jobs have been reported yet.
          </p>
        ) : (
          <div className="divide-y divide-gray-100">
            <div className="hidden grid-cols-[minmax(12rem,2fr)_196px_minmax(7rem,1fr)_minmax(11rem,1fr)_minmax(11rem,1fr)] gap-4 bg-gray-50 px-6 py-3 text-xs font-semibold text-gray-600 xl:grid">
              <span>Job</span>
              <div>
                <span>Last 7 days</span>
                <div className="mt-1 flex font-normal text-gray-500" aria-hidden="true">
                  {jobs[0].dailyHistory.map((day) => (
                    <span key={day.date} className="w-7 text-center">
                      {historyDate(day.date, { day: 'numeric' })}
                    </span>
                  ))}
                </div>
              </div>
              <span>Latest result</span>
              <span>Finished</span>
              <span>Last report</span>
            </div>
            {jobs.map((job) => (
              <div
                key={job.id}
                className="relative grid gap-4 px-6 py-4 transition-colors hover:z-20 hover:bg-gray-50 focus-within:z-20 xl:grid-cols-[minmax(12rem,2fr)_196px_minmax(7rem,1fr)_minmax(11rem,1fr)_minmax(11rem,1fr)] xl:items-center"
              >
                <div className="min-w-0">
                  <Link
                    to={`/dashboard/jobs/${job.id}`}
                    className="font-medium text-blue-700 break-words before:absolute before:inset-0 focus-visible:outline-2 focus-visible:outline-blue-600"
                  >
                    {job.name}
                  </Link>
                </div>
                <div className="relative z-10 w-fit">
                  <div className="text-xs text-gray-500 xl:hidden">Last 7 days</div>
                  <div className="mt-1 flex text-xs text-gray-500 xl:hidden" aria-hidden="true">
                    {job.dailyHistory.map((day) => (
                      <span key={day.date} className="w-7 text-center">
                        {historyDate(day.date, { day: 'numeric' })}
                      </span>
                    ))}
                  </div>
                  <div className="flex">
                    {job.dailyHistory.map((day, index) => (
                      <HistoryDay
                        key={day.date}
                        day={day}
                        index={index}
                        total={job.dailyHistory.length}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <span className="mr-2 text-xs text-gray-500 xl:hidden">Latest result</span>
                  <JobOutcome value={job.state?.latestOutcome} />
                </div>
                <div className="text-sm">
                  <span className="mr-2 text-xs text-gray-500 xl:hidden">Finished</span>
                  <JobTime value={job.state?.latestFinishedAt} />
                </div>
                <div className="text-sm">
                  <span className="mr-2 text-xs text-gray-500 xl:hidden">Last report</span>
                  <JobTime value={job.state?.lastReportReceivedAt} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
